import React from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { CalendarDays, CheckCircle2, Link2Off, Loader2, ShieldCheck, XCircle } from 'lucide-react';
import { beantwoordUitnodiging, getUitnodiging } from '../services/agendaService';

/**
 * Beveiligde webgateway voor actie-pad C van het Agenda Matrix Center
 * (migratie 192): iemand zonder account bekijkt de uitnodiging en bevestigt
 * met één klik. Openen bevestigt niets — pas de knop doet dat, zodat
 * linkscanners en voorvertoningen geen akkoord kunnen geven.
 */
const Schil: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-gray-50 px-4 py-8">
    <div className="mx-auto max-w-md">{children}</div>
  </div>
);

const wanneer = (start: string, eind: string) =>
  `${new Date(start).toLocaleString('nl-NL', { dateStyle: 'full', timeStyle: 'short' })}–${new Date(eind).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`;

export const AgendaUitnodigingPage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const uitnodiging = useQuery({
    queryKey: ['agenda-uitnodiging', token],
    queryFn: () => getUitnodiging(token),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const antwoord = useMutation({
    mutationFn: (akkoord: boolean) => beantwoordUitnodiging(token, akkoord),
    onSuccess: () => uitnodiging.refetch(),
  });

  if (uitnodiging.isLoading) {
    return (
      <Schil>
        <p className="flex items-center justify-center gap-2 text-gray-500 py-16">
          <Loader2 className="w-5 h-5 animate-spin" /> Uitnodiging laden…
        </p>
      </Schil>
    );
  }

  const u = uitnodiging.data;
  if (!u) {
    return (
      <Schil>
        <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
          <Link2Off className="w-10 h-10 mx-auto text-gray-400 mb-3" />
          <h1 className="text-lg font-semibold text-gray-900">Deze link is niet geldig</h1>
          <p className="text-sm text-gray-600 mt-1">Controleer of u de volledige link uit de e-mail of SMS heeft geopend.</p>
        </div>
      </Schil>
    );
  }

  const open = u.status === 'UITGENODIGD';
  const fout = antwoord.error ? (isAxiosError(antwoord.error) ? antwoord.error.response?.data?.message : null) ?? 'Er ging iets mis. Probeer het opnieuw.' : null;

  return (
    <Schil>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-6 py-5 border-b border-gray-100">
          <p className="text-xs uppercase tracking-wide text-gray-500">Uitnodiging via BeLagom</p>
          <h1 className="text-xl font-semibold text-gray-900 mt-1">{u.titel}</h1>
          <p className="text-sm text-gray-600 mt-1">
            {u.professional} namens {u.organisatie}
          </p>
        </div>
        <div className="px-6 py-5 space-y-3 text-sm text-gray-700">
          <p className="flex items-start gap-2">
            <CalendarDays className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <span className="capitalize">{wanneer(u.start_tijd, u.eind_tijd)}</span>
          </p>
          <p className="text-gray-600">{u.locatie}</p>
          {u.omschrijving && <p className="whitespace-pre-line">{u.omschrijving}</p>}

          {u.status === 'BEVESTIGD' && (
            <p className="flex items-center gap-2 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-emerald-800">
              <CheckCircle2 className="w-4 h-4" /> U heeft deze afspraak bevestigd. Tot dan!
            </p>
          )}
          {u.status === 'GEWEIGERD' && (
            <p className="flex items-center gap-2 rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-gray-700">
              <XCircle className="w-4 h-4" /> U heeft deze uitnodiging geweigerd.
            </p>
          )}
          {(u.status === 'VERLOPEN' || u.status === 'VERVALLEN') && (
            <p className="flex items-center gap-2 rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-gray-700">
              <Link2Off className="w-4 h-4" />
              {u.status === 'VERLOPEN' ? 'Deze uitnodiging is verlopen.' : 'Deze afspraak gaat niet meer door.'}
            </p>
          )}
          {fout && <p className="text-sm text-red-700">{fout}</p>}
        </div>
        {open && (
          <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={() => antwoord.mutate(true)}
              disabled={antwoord.isPending}
              className="flex-1 rounded-lg bg-navy-900 text-white px-4 py-2.5 text-sm font-medium hover:bg-navy-800 disabled:opacity-50"
            >
              Bevestigen
            </button>
            <button
              type="button"
              onClick={() => antwoord.mutate(false)}
              disabled={antwoord.isPending}
              className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Weigeren
            </button>
          </div>
        )}
      </div>
      <p className="flex items-start gap-2 text-xs text-gray-500 mt-4 px-1">
        <ShieldCheck className="w-4 h-4 flex-shrink-0" />
        Deze link is persoonlijk. Uw bevestiging wordt met tijdstip vastgelegd; de afspraak valt onder de facturatie van de genoemde organisatie.
      </p>
    </Schil>
  );
};

export default AgendaUitnodigingPage;
